import {
  Add as AddIcon,
  Remove as MinusIcon,
  /* AutoAwesome as SuggestIcon, -- hidden 2026-10-12 with "Suggest with AI" */
  DataObject as BracketIcon,
  Delete as DeleteIcon,
  DescriptionOutlined as DocsIcon,
  FlagOutlined as FlagIcon,
  Language as WebIcon,
  Star as DefaultIcon,
  StarBorder as SetDefaultIcon,
  ExpandMore as ExpandIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import Visible from "components/common/Visible";
import { ingestionKeyForRuleField } from "components/common/submissionReviewFields";
import {
  FIELD_OPTIONS,
  isGroup,
  isLayerField,
  OPERATORS,
} from "components/triagePipeline/triageDefinitions";
import { AUTO_TRIAGE, AI_CARRY_FORWARD_AGENT_SEARCH } from "flags";
import { useSnackbar } from "notistack";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  useRetrievePreferencesQuery,
  useUpdatePreferencesMutation,
} from "services/userService";
import {
  defaultSourcing,
  hasBothSources,
  normaliseSourcing,
  SOURCE_SUBMISSION,
  SOURCE_WEB,
  toggleSourceIn,
} from "utils/fieldSourcing";

// How the child rules of a combined rule are joined. One combinator per group, so the
// expression never needs parentheses. The _not variants negate the rules after the first.
const COMBINATORS = [
  { value: "and", label: "AND" },
  { value: "or", label: "OR" },
  { value: "and_not", label: "AND NOT" },
  { value: "or_not", label: "OR NOT" },
];

const emptyRule = () => ({
  field_name: "insured",
  description: "",
  operator: "exists",
  value: "",
  required: true,
  weight: 1,
  sourcing: defaultSourcing(),
});

// A combined rule is a normal entry in ruleset.rules marked with kind: "group".
// Existing rules have no `kind`, so they keep reading as leaves — no migration needed.
const emptyGroup = () => ({
  kind: "group",
  name: "",
  combinator: "and",
  required: true,
  weight: 1,
  rules: [emptyRule(), emptyRule()],
});

// Nesting is how brackets are expressed: (X AND Y) AND (Z OR X). Capped so the editor
// stays readable — deeper logic is better split into separate rulesets.
const MAX_GROUP_DEPTH = 3;

const emptyRuleset = () => ({
  id: `rs-${Date.now()}`,
  name: "",
  version: "1.0",
  isDefault: false,
  rules: [emptyRule()],
});

// ─── Rule row (table-style) ───────────────────────────────────────────────────

// Compact icon toggle for the per-rule sourcing controls. Filled tint when active,
// dimmed outline when not — the tooltip carries the words the icon replaces. With both
// sources on, a small star rides the winning source (same idiom as the default-ruleset
// star): filled on the winner, faint on the other, its own click target to move it.
const SourceToggle = ({
  Icon,
  title,
  active,
  color = "primary",
  onClick,
  star = null, // null: no badge | {won: bool, title, onClick}
}) => (
  <Box sx={{ position: "relative", display: "inline-flex" }}>
    <Tooltip arrow title={title}>
      <Box
        component={"button"}
        type={"button"}
        onClick={onClick}
        sx={(theme) => ({
          width: 28,
          height: 28,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: active ? `${color}.main` : "divider",
          bgcolor: active
            ? alpha(theme.palette[color].main, 0.12)
            : "transparent",
          color: active ? `${color}.main` : "text.disabled",
          cursor: "pointer",
          p: 0,
          transition: "all .15s",
          "&:hover": {
            borderColor: `${color}.main`,
            color: `${color}.main`,
          },
        })}
      >
        <Icon sx={{ fontSize: "1rem" }} />
      </Box>
    </Tooltip>
    {star && (
      <Tooltip arrow title={star.title}>
        <Box
          component={"button"}
          type={"button"}
          onClick={(e) => {
            e.stopPropagation();
            star.onClick();
          }}
          sx={{
            position: "absolute",
            top: -7,
            right: -7,
            width: 16,
            height: 16,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            border: "none",
            bgcolor: "background.paper",
            color: star.won ? "warning.main" : "text.disabled",
            opacity: star.won ? 1 : 0.45,
            cursor: "pointer",
            p: 0,
            transition: "all .15s",
            "&:hover": { opacity: 1, color: "warning.main" },
          }}
        >
          {star.won ? (
            <DefaultIcon sx={{ fontSize: "0.8rem" }} />
          ) : (
            <SetDefaultIcon sx={{ fontSize: "0.8rem" }} />
          )}
        </Box>
      </Tooltip>
    )}
  </Box>
);

// Scoring weight — a narrow numeric input shown on TOP-LEVEL entries only, because a
// combined rule scores as one unit and nested weights are ignored by the backend.
// The numbers inside the threshold sentence. Grey prose at 11.5px buries the two figures a
// reader is actually looking for, so they carry the body colour and full weight while the
// words around them stay quiet.
const Figure = ({ children }) => (
  <Box
    component={"span"}
    sx={{
      fontWeight: 700,
      color: "text.primary",
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </Box>
);

// A weight of 1 unless the entry says otherwise, matching what the editor writes and what
// the backend defaults to. Anything unparseable or negative reads as 1 rather than 0, so a
// half-typed value cannot silently drop an entry out of the score.
// The rules table's column widths, in one place. The header and every row read them, so a
// change to one column cannot leave the other behind — which is exactly how the WEIGHT
// header ended up 56px wide over a 150px control.
const COLS = {
  number: 22,
  operator: 150,
  weight: 150,
  sources: 92,
  remove: 28,
};

const weightOf = (entry) => {
  const raw = entry?.weight;
  // "" and null are handled before Number(), which coerces an empty string to 0 — that
  // made the total dip while someone was clearing a weight box, as if the entry had
  // stopped scoring. Blur restores 1 anyway, so 1 is the honest reading in the meantime.
  if (raw === "" || raw == null) return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 1;
};

// The ruleset's total weight — what a percentage threshold is a percentage OF, and the
// denominator for each entry's share. Top-level entries only: nested weights do not score.
export const rulesetTotalWeight = (ruleset) =>
  (ruleset?.rules || []).reduce((sum, entry) => sum + weightOf(entry), 0);

// An entry's share of the ruleset, as a percentage. A weight of 4 out of 6 is worth more
// than "4" conveys on its own, and the share is the number a reader reasons about.
export const weightShare = (entry, total) =>
  total > 0 ? Math.round((weightOf(entry) / total) * 100) : 0;

// A percentage threshold expressed in this ruleset's own points: 80% of 6 is 4.8. Trimmed
// to one decimal, and to none when it lands on a whole number.
export const thresholdPoints = (threshold, total) => {
  if (threshold == null || !(total > 0)) return null;
  const points = (threshold / 100) * total;
  return Number(points.toFixed(1));
};

// A number with − and + on either side. Typing still works; the buttons are for the common
// case of nudging a weight or a threshold by one.
const NumberStepper = ({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  ariaLabel,
  width = 44,
  suffix = null,
  onBlur = undefined,
}) => {
  const clamp = (n) =>
    Math.min(max ?? Number.MAX_SAFE_INTEGER, Math.max(min, n));
  const nudge = (by) => onChange(clamp((Number(value) || 0) + by));
  return (
    <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
      <IconButton
        icon={MinusIcon}
        scale={"small"}
        aria-label={`Decrease ${ariaLabel}`}
        onClick={() => nudge(-step)}
        isDisabled={Number(value) <= min}
        sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: "3px" }}
      />
      <TextField
        size={"small"}
        type={"number"}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? "" : Number(e.target.value))
        }
        inputProps={{
          min,
          max,
          step,
          "aria-label": ariaLabel,
          style: {
            fontSize: "0.82rem",
            fontWeight: 500,
            textAlign: "center",
            padding: "4px 2px",
          },
          // The browser's own up/down arrows duplicate the − and + either side of this
          // box, and they crowd a 44px field.
          sx: {
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            MozAppearance: "textfield",
          },
        }}
        onBlur={onBlur}
        InputProps={suffix ? { endAdornment: suffix } : undefined}
        sx={{ width, flexShrink: 0 }}
      />
      <IconButton
        icon={AddIcon}
        scale={"small"}
        aria-label={`Increase ${ariaLabel}`}
        onClick={() => nudge(step)}
        isDisabled={max != null && Number(value) >= max}
        sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: "3px" }}
      />
    </Stack>
  );
};

export const WeightInput = ({ value, onChange, share = null }) => (
  // Two tooltips, not one: the number is the weight itself, the bar beside it is that
  // weight as a share of the ruleset. One tooltip over both said "percentage" while
  // hovering a raw weight.
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
    <Tooltip arrow title={"Weight"}>
      {/* The span is load-bearing: the stepper's TextField is the app's own plain
          function component with no forwardRef, and MUI Tooltip needs a child that can
          hold one — without it React logs "Function components cannot be given refs"
          for every rule row on the page. */}
      <span style={{ display: "inline-flex" }}>
        <NumberStepper
          value={value ?? 1}
          onChange={onChange}
          onBlur={(e) => {
            // Correct on blur, not on change: the box has to stay clearable while
            // typing, and the backend reads a missing weight as 1, so an empty or
            // negative weight must never reach the ruleset.
            const n = Number(e.target.value);
            if (e.target.value === "" || !Number.isFinite(n) || n < 0)
              onChange(1);
          }}
          min={0}
          ariaLabel={"Scoring weight"}
        />
      </span>
    </Tooltip>
    {share != null && (
      <Tooltip arrow title={"Percentage weight"}>
        <Box sx={{ width: 34, flexShrink: 0, cursor: "help" }}>
          <Typography
            data-testid={"weight-share"}
            sx={{
              fontSize: 11,
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.2,
            }}
          >
            {`${share}%`}
          </Typography>
          <Box
            sx={(theme) => ({
              mt: 0.4,
              height: 3,
              borderRadius: 2,
              bgcolor: theme.palette.divider,
            })}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 2,
                bgcolor: "success.main",
                width: `${Math.max(0, Math.min(100, share))}%`,
              }}
            />
          </Box>
        </Box>
      </Tooltip>
    )}
  </Box>
);

const RuleRow = ({
  rule,
  index,
  onChange,
  onDelete,
  agentSearchEnabled,
  topLevel = false,
  totalWeight = 0,
}) => {
  const isExists = rule.operator === "exists";
  // Reused by CombinedRuleBlock for nested children, so every rule component — at any
  // depth — gets its own sourcing. Layer fields are code-evaluated against the extracted
  // tower — the web cannot source them, so no sourcing controls.
  const layerField = isLayerField(rule.field_name);
  const searchableKey =
    !layerField && ingestionKeyForRuleField(rule.field_name);
  const sourcing = normaliseSourcing(rule.sourcing);
  const bothSources = hasBothSources(sourcing);

  const setSourcing = (patch) =>
    onChange({ ...rule, sourcing: { ...sourcing, ...patch } });

  const toggleSource = (source) =>
    onChange({ ...rule, sourcing: toggleSourceIn(sourcing, source) });

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      spacing={1}
      sx={(theme) => ({
        py: "9px",
        px: "12px",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none" },
      })}
    >
      {/* Row number */}
      <Typography
        sx={{
          fontSize: 11.5,
          fontFamily: "monospace",
          color: "text.disabled",
          width: 22,
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        {String(index + 1).padStart(2, "0")}
      </Typography>

      {/* Field select */}
      <Select
        size={"small"}
        value={rule.field_name}
        onChange={(e) => {
          const field_name = e.target.value;
          // A layer field cannot be web-sourced, and its sourcing icons are hidden —
          // so a "web" left over from the previous field would be invisible and
          // unclearable here. Reset it on the switch instead.
          onChange({
            ...rule,
            field_name,
            ...(isLayerField(field_name) && { sourcing: defaultSourcing() }),
          });
        }}
        displayEmpty
        sx={{ flex: "1.3 1 0", fontSize: "0.82rem", minWidth: 0 }}
        renderValue={(v) => {
          const found = FIELD_OPTIONS.find((f) => f.value === v);
          if (found) return found.label;
          if (v) return v;
          return (
            <Box component={"span"} sx={{ color: "text.disabled" }}>
              {"Select field…"}
            </Box>
          );
        }}
      >
        {FIELD_OPTIONS.map((f) => (
          <MenuItem key={f.value} value={f.value} sx={{ fontSize: "0.82rem" }}>
            {f.label}
          </MenuItem>
        ))}
        {/* If current value is not in the list, show it as a disabled hint */}
        {!FIELD_OPTIONS.find((f) => f.value === rule.field_name) &&
          rule.field_name && (
            <MenuItem value={rule.field_name} sx={{ fontSize: "0.82rem" }}>
              {rule.field_name}
            </MenuItem>
          )}
      </Select>

      {/* Operator select */}
      <Select
        size={"small"}
        value={rule.operator}
        onChange={(e) => onChange({ ...rule, operator: e.target.value })}
        sx={{ width: 150, flexShrink: 0, fontSize: "0.82rem" }}
      >
        {OPERATORS.map((op) => (
          <MenuItem
            key={op.value}
            value={op.value}
            sx={{ fontSize: "0.82rem" }}
          >
            {op.label}
          </MenuItem>
        ))}
      </Select>

      {/* Value input */}
      <TextField
        size={"small"}
        value={rule.value ?? ""}
        onChange={(e) => onChange({ ...rule, value: e.target.value || null })}
        disabled={isExists}
        placeholder={isExists ? "— any value —" : "Enter value"}
        sx={{ flex: "1.1 1 0", minWidth: 0 }}
        inputProps={{ style: { fontSize: "0.82rem" } }}
      />

      {/* Scoring weight — top-level entries only; nested weights would not score. The
          column is held open either way so the sourcing icons line up down the table. */}
      <Box
        sx={{
          width: COLS.weight,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        {topLevel && (
          <WeightInput
            value={rule.weight}
            onChange={(weight) => onChange({ ...rule, weight })}
            share={weightShare(rule, totalWeight)}
          />
        )}
      </Box>

      {/* Sourcing — icons only, and only where they mean something: any named field
          shows Docs/Web; the preference and flag appear once both sources are on. */}
      {agentSearchEnabled && (
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={0.5}
          sx={{ width: COLS.sources, flexShrink: 0 }}
        >
          {searchableKey && (
            <>
              <SourceToggle
                Icon={DocsIcon}
                title={"Documents as a source"}
                active={sourcing.sources.includes(SOURCE_SUBMISSION)}
                onClick={() => toggleSource(SOURCE_SUBMISSION)}
                star={
                  bothSources
                    ? {
                        won: sourcing.prefer !== SOURCE_WEB,
                        title: "Prefer document source",
                        onClick: () =>
                          setSourcing({ prefer: SOURCE_SUBMISSION }),
                      }
                    : null
                }
              />
              <SourceToggle
                Icon={WebIcon}
                title={"Web search as a source"}
                active={sourcing.sources.includes(SOURCE_WEB)}
                onClick={() => toggleSource(SOURCE_WEB)}
                star={
                  bothSources
                    ? {
                        won: sourcing.prefer === SOURCE_WEB,
                        title: "Prefer web source",
                        onClick: () => setSourcing({ prefer: SOURCE_WEB }),
                      }
                    : null
                }
              />
              {bothSources && (
                <SourceToggle
                  Icon={FlagIcon}
                  title={"Flag the field when the two sources disagree"}
                  active={Boolean(sourcing.flag_disagreement)}
                  color={"warning"}
                  onClick={() =>
                    setSourcing({
                      flag_disagreement: !sourcing.flag_disagreement,
                    })
                  }
                />
              )}
            </>
          )}
        </Stack>
      )}

      {/* Delete */}
      <IconButton
        icon={DeleteIcon}
        onClick={onDelete}
        size={"small"}
        tooltip={"Remove rule"}
        sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
      />
    </Stack>
  );
};

// ─── Combined rule (named group of rules joined by one combinator) ────────────

const CombinedRuleBlock = ({
  group,
  index,
  onChange,
  onDelete,
  depth = 0,
  agentSearchEnabled,
  totalWeight = 0,
}) => {
  const children = group.rules || [];
  const canNest = depth + 1 < MAX_GROUP_DEPTH;

  const updateChild = (idx, rule) => {
    const rules = [...children];
    rules[idx] = rule;
    onChange({ ...group, rules });
  };

  const deleteChild = (idx) =>
    onChange({ ...group, rules: children.filter((_, i) => i !== idx) });

  const addChild = () =>
    onChange({ ...group, rules: [...children, emptyRule()] });

  const addNestedGroup = () =>
    onChange({ ...group, rules: [...children, emptyGroup()] });

  const setCombinator = (combinator) => onChange({ ...group, combinator });

  return (
    <Box
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none" },
        // Nested brackets get a visible left rail and a deeper tint so the level is
        // readable at a glance rather than looking like a flat sub-list.
        bgcolor: alpha(theme.palette.primary.main, depth === 0 ? 0.02 : 0.05),
        borderLeft:
          depth === 0
            ? "none"
            : `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        borderRadius: depth === 0 ? 0 : "6px",
        my: depth === 0 ? 0 : "6px",
        mr: depth === 0 ? 0 : "12px",
      })}
    >
      {/* Group header — the name the reviewer gives the combined rule */}
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={1}
        sx={{ py: "9px", px: "12px" }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            fontFamily: "monospace",
            color: "text.disabled",
            width: 22,
            flexShrink: 0,
            userSelect: "none",
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </Typography>
        <Tooltip
          arrow
          title={
            depth === 0
              ? "A combined rule joins its rules with one operator (AND / OR / NOT). " +
                "Add a sub combined rule to bracket part of the logic — e.g. (X AND Y) AND (Z OR X)."
              : "A sub combined rule is a bracket: it is evaluated on its own first, " +
                "then joined into the parent by the parent's operator."
          }
        >
          <Chip
            size={"small"}
            icon={<BracketIcon sx={{ fontSize: "0.78rem !important" }} />}
            label={depth === 0 ? "Combined rule" : "Sub combined rule"}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontWeight: 600,
              flexShrink: 0,
              cursor: "help",
            }}
          />
        </Tooltip>
        <TextField
          size={"small"}
          value={group.name ?? ""}
          onChange={(e) => onChange({ ...group, name: e.target.value })}
          placeholder={
            depth === 0 ? "Name this combined rule" : "Name (optional)"
          }
          sx={{ flex: "0 1 240px", minWidth: 0 }}
          inputProps={{ style: { fontSize: "0.82rem", fontWeight: 600 } }}
        />
        {/* A spacer, not the expression. Rendering the constructed expression here left
            it truncated to something like "((indus…" and crowding the stepper, and the
            child rules are listed directly below anyway. The space still has to be held
            so the weight lands in the same column as a rule row's. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <Box
          sx={{
            width: COLS.weight,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {depth === 0 && (
            <WeightInput
              value={group.weight}
              onChange={(weight) => onChange({ ...group, weight })}
              share={weightShare(group, totalWeight)}
            />
          )}
        </Box>
        {agentSearchEnabled && (
          <Box sx={{ width: COLS.sources, flexShrink: 0 }} />
        )}
        <IconButton
          icon={DeleteIcon}
          onClick={onDelete}
          size={"small"}
          tooltip={"Remove combined rule"}
          sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
        />
      </Stack>

      {/* Children, with the combinator shown in each gap */}
      <Box sx={{ pl: "22px" }}>
        {children.map((child, idx) => (
          <Box key={idx}>
            {idx > 0 && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                sx={{ px: "12px", py: "2px" }}
              >
                <Select
                  size={"small"}
                  value={group.combinator || "and"}
                  onChange={(e) => setCombinator(e.target.value)}
                  sx={{
                    width: 120,
                    flexShrink: 0,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    "& .MuiSelect-select": { py: "3px" },
                  }}
                >
                  {COMBINATORS.map((c) => (
                    <MenuItem
                      key={c.value}
                      value={c.value}
                      sx={{ fontSize: "0.78rem", fontWeight: 700 }}
                    >
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
                <Box
                  sx={{
                    flex: 1,
                    borderTop: "1px dashed",
                    borderColor: "divider",
                  }}
                />
              </Stack>
            )}
            {isGroup(child) ? (
              <CombinedRuleBlock
                group={child}
                index={idx}
                depth={depth + 1}
                onChange={(updated) => updateChild(idx, updated)}
                onDelete={() => deleteChild(idx)}
                agentSearchEnabled={agentSearchEnabled}
              />
            ) : (
              <RuleRow
                rule={child}
                index={idx}
                onChange={(updated) => updateChild(idx, updated)}
                onDelete={() => deleteChild(idx)}
                agentSearchEnabled={agentSearchEnabled}
              />
            )}
          </Box>
        ))}

        <Stack direction={"row"} spacing={0.75} sx={{ px: "12px", py: "10px" }}>
          <MuiButton
            size={"small"}
            variant={"outlined"}
            startIcon={<AddIcon sx={{ fontSize: "0.8rem !important" }} />}
            onClick={addChild}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              bgcolor: "background.paper",
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
          >
            {"Add rule"}
          </MuiButton>
          {canNest && (
            <MuiButton
              size={"small"}
              variant={"outlined"}
              startIcon={<BracketIcon sx={{ fontSize: "0.8rem !important" }} />}
              onClick={addNestedGroup}
              sx={(theme) => ({
                fontSize: "0.75rem",
                textTransform: "none",
                color: "primary.main",
                borderColor: alpha(theme.palette.primary.main, 0.5),
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: "primary.main",
                },
              })}
            >
              {"Add sub combined rule"}
            </MuiButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// ─── Single ruleset accordion ─────────────────────────────────────────────────

const RulesetEditor = ({
  ruleset,
  index,
  onChange,
  onDelete,
  onSetDefault,
  agentSearchEnabled,
}) => {
  const displayName = ruleset.name || `Ruleset ${index + 1}`;
  // What the threshold is a percentage of, and the denominator for each entry's share.
  const totalWeight = rulesetTotalWeight(ruleset);

  const updateRule = (idx, rule) => {
    const rules = [...ruleset.rules];
    rules[idx] = rule;
    onChange({ ...ruleset, rules });
  };

  const deleteRule = (idx) => {
    onChange({ ...ruleset, rules: ruleset.rules.filter((_, i) => i !== idx) });
  };

  const addRule = () => {
    onChange({ ...ruleset, rules: [...ruleset.rules, emptyRule()] });
  };

  const addCombinedRule = () => {
    onChange({ ...ruleset, rules: [...ruleset.rules, emptyGroup()] });
  };

  return (
    <Accordion
      variant={"outlined"}
      sx={{ "&:before": { display: "none" }, borderRadius: "8px !important" }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon />}
        sx={{ px: 2, "& .MuiAccordionSummary-content": { my: 1.5 } }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1.5}
          sx={{ width: "100%", mr: 1 }}
        >
          <Stack flex={1} spacing={0.25} minWidth={0}>
            <Typography fontWeight={600} variant={"body2"} noWrap>
              {displayName}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {`v${ruleset.version} · ${ruleset.rules.length} rule${
                ruleset.rules.length !== 1 ? "s" : ""
              }`}
            </Typography>
          </Stack>
          {ruleset.isDefault ? (
            <Chip
              label={"Default"}
              size={"small"}
              icon={
                <DefaultIcon
                  sx={{
                    fontSize: "0.75rem !important",
                    color: "success.dark !important",
                  }}
                />
              }
              sx={(theme) => ({
                fontWeight: 600,
                fontSize: "0.68rem",
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: "success.dark",
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                "& .MuiChip-icon": { color: "success.dark" },
              })}
            />
          ) : (
            <IconButton
              icon={SetDefaultIcon}
              tooltip={"Set as default"}
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault();
              }}
              size={"small"}
            />
          )}
          <IconButton
            icon={DeleteIcon}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            size={"small"}
            tooltip={"Delete ruleset"}
          />
        </Stack>
      </AccordionSummary>

      <Divider />

      <AccordionDetails sx={{ p: "16px 20px" }}>
        {/* Name field */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
              mb: 0.75,
            }}
          >
            {"Name"}
          </Typography>
          <TextField
            value={ruleset.name}
            onChange={(e) => onChange({ ...ruleset, name: e.target.value })}
            size={"small"}
            fullWidth
            placeholder={`Ruleset ${index + 1}`}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
              mb: 0.75,
            }}
          >
            {"Description"}
          </Typography>
          <TextField
            value={ruleset.description ?? ""}
            onChange={(e) =>
              onChange({ ...ruleset, description: e.target.value || null })
            }
            size={"small"}
            fullWidth
            placeholder={
              "Base triage criteria applied to submissions without a line-specific ruleset."
            }
            inputProps={{ style: { fontSize: "0.82rem" } }}
          />
        </Box>

        {/* In-scope threshold */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
              mb: 0.75,
            }}
          >
            {"In scope at"}
          </Typography>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            spacing={2}
          >
            <NumberStepper
              value={ruleset.in_scope_threshold ?? ""}
              onChange={(next) =>
                onChange({
                  ...ruleset,
                  // Empty clears it, which restores the original behaviour: a failing
                  // required rule hard-stops the submission.
                  in_scope_threshold:
                    next === "" ? null : Math.min(100, Math.max(0, next)),
                })
              }
              min={0}
              max={100}
              ariaLabel={"In-scope threshold"}
              width={72}
              suffix={
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", ml: "-2px" }}
                >
                  {"%"}
                </Typography>
              }
            />
            <Typography
              sx={{
                fontSize: 11.5,
                color: "text.secondary",
                textAlign: "right",
                maxWidth: 300,
              }}
            >
              {ruleset.in_scope_threshold == null ? (
                "Without a threshold, a submission is out of scope as soon as one required rule fails."
              ) : (
                <>
                  {"A submission scoring below "}
                  <Figure>{`${ruleset.in_scope_threshold}%`}</Figure>
                  {" is out of scope."}
                  {totalWeight > 0 && (
                    <>
                      {" On this ruleset that is "}
                      <Figure>
                        {`${thresholdPoints(
                          ruleset.in_scope_threshold,
                          totalWeight
                        )} of ${totalWeight}`}
                      </Figure>
                      {" points."}
                    </>
                  )}
                </>
              )}
            </Typography>
          </Stack>
          <Slider
            value={ruleset.in_scope_threshold ?? 0}
            onChange={(_, next) =>
              onChange({ ...ruleset, in_scope_threshold: next })
            }
            min={0}
            max={100}
            step={1}
            marks={[
              { value: 0, label: "0" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            aria-label={"In-scope threshold slider"}
            size={"small"}
            sx={{
              mt: 1,
              "& .MuiSlider-markLabel": { fontSize: 10.5 },
              // A neutral rail rather than a tinted one, so the filled part reads as the
              // threshold and the remainder as empty.
              "& .MuiSlider-rail": (theme) => ({
                opacity: 1,
                backgroundColor: theme.palette.divider,
              }),
              "& .MuiSlider-mark": { display: "none" },
            }}
          />
        </Box>

        {/* Rules section */}
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
            }}
          >
            {`Rules · ${ruleset.rules.length}`}
          </Typography>
          {/* "Suggest with AI" hidden 2026-10-12: the button has no handler yet. To bring
              it back, uncomment it and the SuggestIcon import at the top.
          <MuiButton
            size={"small"}
            variant={"outlined"}
            color={"inherit"}
            startIcon={<SuggestIcon sx={{ fontSize: "0.75rem !important" }} />}
            sx={{ fontSize: "0.78rem", textTransform: "none" }}
          >
            {"Suggest with AI"}
          </MuiButton>
          */}
        </Stack>

        {/* Rules table */}
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            overflow: "hidden",
          })}
        >
          {/* Column headers */}
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            sx={(theme) => ({
              px: "12px",
              py: "7px",
              bgcolor: alpha(theme.palette.text.primary, 0.02),
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Box sx={{ width: COLS.number, flexShrink: 0 }} />
            <Typography
              sx={{
                flex: "1.3 1 0",
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.disabled",
              }}
            >
              {"Field"}
            </Typography>
            <Typography
              sx={{
                width: COLS.operator,
                flexShrink: 0,
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.disabled",
              }}
            >
              {"Operator"}
            </Typography>
            <Typography
              sx={{
                flex: "1.1 1 0",
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.disabled",
              }}
            >
              {"Value"}
            </Typography>
            <Typography
              sx={{
                width: COLS.weight,
                flexShrink: 0,
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.disabled",
              }}
            >
              {"Weight"}
            </Typography>
            {agentSearchEnabled && (
              <Typography
                sx={{
                  width: COLS.sources,
                  flexShrink: 0,
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.disabled",
                }}
              >
                {"Sources"}
              </Typography>
            )}
            {/* delete col placeholder */}
            <Box sx={{ width: COLS.remove, flexShrink: 0 }} />
          </Stack>

          {/* Rule rows */}
          {ruleset.rules.map((rule, idx) =>
            isGroup(rule) ? (
              <CombinedRuleBlock
                key={idx}
                index={idx}
                group={rule}
                onChange={(updated) => updateRule(idx, updated)}
                onDelete={() => deleteRule(idx)}
                agentSearchEnabled={agentSearchEnabled}
                totalWeight={totalWeight}
              />
            ) : (
              <RuleRow
                key={idx}
                index={idx}
                rule={rule}
                onChange={(updated) => updateRule(idx, updated)}
                onDelete={() => deleteRule(idx)}
                agentSearchEnabled={agentSearchEnabled}
                topLevel
                totalWeight={totalWeight}
              />
            )
          )}
        </Box>

        <Stack direction={"row"} spacing={1} sx={{ mt: 1.25 }}>
          <MuiButton
            size={"small"}
            variant={"outlined"}
            startIcon={<AddIcon sx={{ fontSize: "0.8rem !important" }} />}
            onClick={addRule}
            sx={{
              fontSize: "0.78rem",
              textTransform: "none",
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
          >
            {"Add rule"}
          </MuiButton>
          <Tooltip
            arrow
            title={
              "Join several rules with one operator (AND / OR / NOT). Nest a sub combined " +
              "rule inside it to bracket part of the logic — e.g. (X AND Y) AND (Z OR X)."
            }
          >
            <MuiButton
              size={"small"}
              variant={"outlined"}
              startIcon={<BracketIcon sx={{ fontSize: "0.8rem !important" }} />}
              onClick={addCombinedRule}
              sx={(theme) => ({
                fontSize: "0.78rem",
                textTransform: "none",
                color: "primary.main",
                borderColor: alpha(theme.palette.primary.main, 0.5),
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: "primary.main",
                },
              })}
            >
              {"Add combined rule"}
            </MuiButton>
          </Tooltip>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const TriageRulesetsConfig = forwardRef(({ onCountChange }, ref) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: preferences } = useRetrievePreferencesQuery();
  const [updatePreferences] = useUpdatePreferencesMutation();

  const [rulesets, setRulesets] = useState([]);
  const [autoTriage, setAutoTriage] = useState(false);
  // Only the master switch lives here. Which fields are searched, which source wins and
  // whether disagreements are flagged are all per rule component.
  const [agentSearch, setAgentSearch] = useState({ enabled: false });
  const [dirty, setDirty] = useState(false);

  // The per-rule mode controls only appear once the master switch is on — configuring
  // modes on rules that will never be searched is just noise.
  const agentSearchEnabled = agentSearch.enabled;

  useEffect(() => {
    if (!dirty && preferences) {
      if (preferences.triageRulesets || preferences.triage_rulesets) {
        setRulesets(preferences.triageRulesets ?? preferences.triage_rulesets);
      }
      setAutoTriage(preferences.autoTriage ?? false);
      if (preferences.agentSearchIngestionFields) {
        setAgentSearch((prev) => ({
          ...prev,
          ...preferences.agentSearchIngestionFields,
        }));
      }
    }
  }, [preferences, dirty]);

  const update = (updated) => {
    setRulesets(updated);
    setDirty(true);
  };

  const updateRuleset = (idx, ruleset) => {
    const next = [...rulesets];
    next[idx] = ruleset;
    update(next);
  };

  const setDefault = (idx) => {
    update(rulesets.map((r, i) => ({ ...r, isDefault: i === idx })));
  };

  const deleteRuleset = (idx) => {
    update(rulesets.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    try {
      await updatePreferences({
        ...(preferences || {}),
        triageRulesets: rulesets,
        autoTriage,
        agentSearchIngestionFields: agentSearch,
      }).unwrap();
      setDirty(false);
      enqueueSnackbar("Triage rulesets saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(`Failed to save: ${e.message}`, { variant: "error" });
    }
  };

  const handleAddRuleset = () => {
    update([...rulesets, emptyRuleset()]);
  };

  useEffect(() => {
    onCountChange?.(rulesets.length);
  }, [rulesets.length, onCountChange]);

  useImperativeHandle(ref, () => ({
    save: handleSave,
    addRuleset: handleAddRuleset,
  }));

  return (
    <Box>
      {/* Auto-triage toggle (feature-flagged) */}
      <Visible byTag={AUTO_TRIAGE} defaultVisible={false}>
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.75,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Switch
            checked={autoTriage}
            onChange={(e) => {
              setAutoTriage(e.target.checked);
              setDirty(true);
            }}
            sx={{ flexShrink: 0 }}
          />
          <Box>
            <Typography variant={"body2"} fontWeight={500}>
              {"Automatically run triage after ingestion"}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {
                "Uses the default ruleset. Runs in the background once extraction completes."
              }
            </Typography>
          </Box>
        </Box>
      </Visible>

      {/* Agent search settings (feature-flagged) */}
      <Visible byTag={AI_CARRY_FORWARD_AGENT_SEARCH} defaultVisible={false}>
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.75,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Switch
              checked={agentSearch.enabled}
              onChange={(e) => {
                setAgentSearch((v) => ({
                  ...v,
                  enabled: e.target.checked,
                }));
                setDirty(true);
              }}
              sx={{ flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant={"body2"} fontWeight={500}>
                {"Web search"}
              </Typography>
              <Typography variant={"caption"} color={"text.secondary"}>
                {"Allows web search for fields."}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Visible>

      {/* Ruleset list */}
      {rulesets.length === 0 ? (
        <Typography
          variant={"body2"}
          color={"text.secondary"}
          sx={{ py: 3, textAlign: "center" }}
        >
          {"No triage rulesets configured"}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rulesets.map((ruleset, idx) => (
            <RulesetEditor
              key={ruleset.id}
              index={idx}
              ruleset={ruleset}
              onChange={(updated) => updateRuleset(idx, updated)}
              onDelete={() => deleteRuleset(idx)}
              onSetDefault={() => setDefault(idx)}
              agentSearchEnabled={agentSearchEnabled}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
});

export default TriageRulesetsConfig;
